import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { getConsent, saveConsent } from "../lib/consent";

/**
 * Bandeau de consentement cookies (bannière + modale personnalisation).
 * Se déclenche automatiquement au premier chargement.
 * Émet un event window "pv:consent" à chaque enregistrement pour que d'autres
 * modules (analytics interne) puissent réagir.
 */
const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (!getConsent()) setOpen(true);
    const handler = () => setOpen(true);
    window.addEventListener("pv:openConsent", handler);
    return () => window.removeEventListener("pv:openConsent", handler);
  }, []);

  const persist = (prefs) => {
    saveConsent(prefs);
    window.dispatchEvent(new CustomEvent("pv:consent", { detail: prefs }));
    setOpen(false);
    setCustomizing(false);
  };

  const acceptAll = () => persist({ analytics: true, essential: true });
  const refuseAll = () => persist({ analytics: false, essential: true });
  const saveCustom = () => persist({ analytics, essential: true });

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6"
      role="dialog"
      aria-labelledby="cookie-title"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-[#171717] border border-[#9A8640] shadow-2xl">
        {!customizing ? (
          <div className="p-6 sm:p-8">
            <h2 id="cookie-title" className="font-serif text-2xl text-[#DDD4B2] mb-2">
              Votre vie privée nous tient à cœur
            </h2>
            <p className="text-sm text-[#DDD4B2]/85 leading-relaxed mb-6">
              Nous utilisons un outil de mesure d'audience (PostHog, chargé par la plateforme technique de notre site)
              pour comprendre comment les visiteurs utilisent passionverre.com et améliorer votre expérience.
              Aucune donnée ne sera transmise sans votre accord.{" "}
              <Link to="/politique-cookies" className="text-[#9A8640] underline hover:text-[#DDD4B2]">
                En savoir plus
              </Link>
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={acceptAll}
                className="flex-1 min-w-[180px] px-5 py-3 text-xs uppercase tracking-[0.15em] bg-[#9A8640] text-[#171717] hover:bg-[#DDD4B2] transition-colors font-medium"
                data-testid="cookie-accept-all"
              >
                Tout accepter
              </button>
              <button
                onClick={() => setCustomizing(true)}
                className="flex-1 min-w-[180px] px-5 py-3 text-xs uppercase tracking-[0.15em] border border-[#9A8640] text-[#DDD4B2] hover:bg-[#9A8640]/10 transition-colors font-medium"
                data-testid="cookie-customize"
              >
                Personnaliser
              </button>
              <button
                onClick={refuseAll}
                className="text-xs uppercase tracking-[0.15em] text-[#DDD4B2]/70 hover:text-[#DDD4B2] underline underline-offset-4 px-2 py-2"
                data-testid="cookie-refuse-all"
              >
                Tout refuser
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between mb-4">
              <h2 className="font-serif text-2xl text-[#DDD4B2]">Personnaliser mes choix</h2>
              <button
                onClick={() => setCustomizing(false)}
                className="text-[#DDD4B2]/60 hover:text-[#DDD4B2]"
                aria-label="Fermer"
                data-testid="cookie-back"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="border border-[#9A8640]/25 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-lg text-[#DDD4B2]">Cookies essentiels</h3>
                    <p className="text-xs text-[#DDD4B2]/70 mt-1">
                      Nécessaires au bon fonctionnement du site (navigation, sécurité, mémorisation de vos préférences de consentement).
                      Ces cookies sont toujours activés.
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#9A8640] shrink-0 mt-1">Obligatoire</span>
                </div>
              </div>

              <div className="border border-[#9A8640]/25 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-lg text-[#DDD4B2]">Mesure d'audience (PostHog)</h3>
                    <p className="text-xs text-[#DDD4B2]/70 mt-1">
                      Nous permet de comprendre quelles pages sont consultées, d'où viennent les visiteurs et comment
                      améliorer votre expérience. Aucune donnée personnelle n'est collectée.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0" data-testid="toggle-analytics">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="sr-only peer"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="w-11 h-6 bg-[#0f0f0f] border border-[#9A8640]/40 peer-checked:bg-[#9A8640] transition-colors relative">
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[#DDD4B2] transition-transform ${analytics ? "translate-x-5" : ""}`} />
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={refuseAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.15em] border border-[#9A8640] text-[#DDD4B2] hover:bg-[#9A8640]/10"
                data-testid="cookie-refuse-in-custom"
              >
                Tout refuser
              </button>
              <button
                onClick={saveCustom}
                className="px-5 py-3 text-xs uppercase tracking-[0.15em] bg-[#9A8640] text-[#171717] hover:bg-[#DDD4B2] font-medium"
                data-testid="cookie-save-choice"
              >
                Enregistrer mes choix
              </button>
              <button
                onClick={acceptAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.15em] border border-[#9A8640] text-[#DDD4B2] hover:bg-[#9A8640]/10"
                data-testid="cookie-accept-in-custom"
              >
                Tout accepter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
